import { useCallback, useState } from "react";
import {
  EMPTY_CALENDAR_FILTERS,
  FACET_KEYS,
  type CalendarFilters,
  type FacetKey,
} from "./calendar-filters";

/**
 * Session-persistent calendar review filters, mirroring the localStorage
 * read/parse/guard shape of {@link useOverlapTolerance}. Selections survive
 * week/day navigation, tab switches, and full reloads, and are namespaced per
 * schedule id so they never bleed across schedules. Malformed or unavailable
 * storage degrades silently to empty filters (the feature still works in-session).
 */
const STORAGE_PREFIX = "bt-calendar-filters";

function storageKey(scheduleId: string): string {
  return `${STORAGE_PREFIX}:${scheduleId}`;
}

/** Parse + shape-validate persisted filters; unknown/broken input → empty. */
function parseFilters(raw: string | null): CalendarFilters {
  if (raw === null) return EMPTY_CALENDAR_FILTERS;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) {
      return EMPTY_CALENDAR_FILTERS;
    }
    const record = parsed as Record<string, unknown>;
    const result: CalendarFilters = { ...EMPTY_CALENDAR_FILTERS };
    for (const key of FACET_KEYS) {
      const value = record[key];
      if (Array.isArray(value)) {
        result[key] = value.filter((v): v is string => typeof v === "string");
      }
    }
    return result;
  } catch {
    return EMPTY_CALENDAR_FILTERS;
  }
}

function readStored(scheduleId: string): CalendarFilters {
  if (typeof window === "undefined") return EMPTY_CALENDAR_FILTERS;
  try {
    return parseFilters(window.localStorage.getItem(storageKey(scheduleId)));
  } catch {
    return EMPTY_CALENDAR_FILTERS;
  }
}

function writeStored(scheduleId: string, filters: CalendarFilters): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(scheduleId), JSON.stringify(filters));
  } catch {
    // Private mode / quota exceeded — persistence degrades, in-session state stays.
  }
}

export interface UseCalendarFiltersResult {
  filters: CalendarFilters;
  /** Replace a facet's entire selection. */
  setFacet: (key: FacetKey, values: string[]) => void;
  /** Add the value if absent, remove it if present. */
  toggleValue: (key: FacetKey, value: string) => void;
  /** Remove a single value from a facet (chip dismissal). */
  removeValue: (key: FacetKey, value: string) => void;
  /** Clear every facet. */
  clearAll: () => void;
}

export function useCalendarFilters(
  scheduleId: string,
  activeRunId?: string,
): UseCalendarFiltersResult {
  const [filters, setFilters] = useState<CalendarFilters>(() =>
    readStored(scheduleId),
  );

  // Re-load when the schedule changes (react-router keeps the page mounted
  // across :id param changes, so a plain initial-state read wouldn't refresh).
  // Adjusting state during render — React's recommended pattern for resetting
  // state on a prop change — avoids a cascading setState-in-effect.
  const [loadedFor, setLoadedFor] = useState(scheduleId);
  if (loadedFor !== scheduleId) {
    setLoadedFor(scheduleId);
    setFilters(readStored(scheduleId));
  }

  // Reset the student facet when the active run changes to a *different* real
  // run — a selected student may be placed differently (or absent) in the new
  // run's assignments. The initial undefined→run transition (first load /
  // run still resolving) preserves any persisted student selection.
  const [loadedRun, setLoadedRun] = useState(activeRunId);
  if (loadedRun !== activeRunId) {
    const changedRun = loadedRun !== undefined && activeRunId !== undefined;
    setLoadedRun(activeRunId);
    if (changedRun) {
      setFilters((prev) => {
        if (prev.students.length === 0) return prev;
        const next = { ...prev, students: [] };
        writeStored(scheduleId, next);
        return next;
      });
    }
  }

  const update = useCallback(
    (mutate: (prev: CalendarFilters) => CalendarFilters) => {
      setFilters((prev) => {
        const next = mutate(prev);
        writeStored(scheduleId, next);
        return next;
      });
    },
    [scheduleId],
  );

  const setFacet = useCallback(
    (key: FacetKey, values: string[]) => {
      update((prev) => ({ ...prev, [key]: values }));
    },
    [update],
  );

  const toggleValue = useCallback(
    (key: FacetKey, value: string) => {
      update((prev) => {
        const has = prev[key].includes(value);
        return {
          ...prev,
          [key]: has
            ? prev[key].filter((v) => v !== value)
            : [...prev[key], value],
        };
      });
    },
    [update],
  );

  const removeValue = useCallback(
    (key: FacetKey, value: string) => {
      update((prev) => ({
        ...prev,
        [key]: prev[key].filter((v) => v !== value),
      }));
    },
    [update],
  );

  const clearAll = useCallback(() => {
    update(() => ({ ...EMPTY_CALENDAR_FILTERS }));
  }, [update]);

  return { filters, setFacet, toggleValue, removeValue, clearAll };
}
